import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Plus, Search, Undo2 } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { useTerminology } from '@/lib/useTerminology'
import { fetchProjectNeighbors, fetchProjects, type ProjectNeighbors } from '../api/projects.api'
import { loadListContext, loadRecentProjects, rememberRecentProject, type RecentProject } from '../project-navigation'
import { ProjectFormDialog } from './ProjectFormDialog'

/**
 * AP-35 Nachtrag 5 — **der Navigationsrahmen rechts im Projektkopf.**
 *
 * Oben das Suchfeld (leer: zuletzt geöffnete Projekte; getippt: Nummer, Name,
 * Kunde), darunter Blättern ‹ ›, ↶ zum zuletzt geöffneten Projekt und „Neu".
 * Status und Kopieren stehen nicht hier (N5-12, N5-13). Die linke Seite des Kopfs
 * bleibt unverändert (Auftraggeber 19.9.2026).
 *
 * Geblättert wird in der Reihenfolge der Projektliste mit ihren zuletzt benutzten
 * Filtern und ihrer Sortierung; am Ende geht es zum Anfang weiter (N5-4).
 *
 * Ohne `projekt` steht der Rahmen im leeren Zustand (Menüpunkt „Projekte"):
 * ‹ › führen zum letzten bzw. ersten Projekt der Liste, „Neu" ist die
 * hervorgehobene Aktion.
 *
 * Tasten: `/` springt ins Suchfeld, `[` und `]` blättern — nicht, während in
 * einem Eingabefeld geschrieben wird.
 */
export function ProjectNavigator({ projekt }: { projekt: RecentProject | null }) {
  const navigate = useNavigate()
  const terms = useTerminology()
  const { user } = useAuthStore()
  const darfAnlegen = erfuelltRecht('projects.project.manage', user?.permissions)

  const [recent, setRecent] = useState<RecentProject[]>(loadRecentProjects)
  const [nachbarn, setNachbarn] = useState<ProjectNeighbors | null>(null)
  const [anlegen, setAnlegen] = useState(false)

  // ── Zuletzt geöffnet ────────────────────────────────────────────────────
  const projektId = projekt?.id
  const projektNummer = projekt?.projectNumber
  const projektName = projekt?.name
  useEffect(() => {
    if (projektId && projektNummer !== undefined && projektName !== undefined) {
      setRecent(rememberRecentProject({ id: projektId, projectNumber: projektNummer, name: projektName }))
    }
  }, [projektId, projektNummer, projektName])
  const zuletzt = recent.find((p) => p.id !== projektId) ?? null

  // ── Nachbarn in der Listenreihenfolge ───────────────────────────────────
  useEffect(() => {
    setNachbarn(null)
    if (!projektId) return
    let aktiv = true
    fetchProjectNeighbors(projektId, loadListContext(user?.id))
      .then((n) => aktiv && setNachbarn(n))
      .catch(() => aktiv && setNachbarn(null))
    return () => {
      aktiv = false
    }
  }, [projektId, user?.id])

  const oeffne = useCallback((id: string) => navigate(`/projects/${id}`), [navigate])

  /** Leerer Zustand: erstes bzw. letztes Projekt der Liste, erst beim Klick geholt. */
  const randProjekt = useCallback(
    async (richtung: 'first' | 'last') => {
      const ctx = loadListContext(user?.id)
      const erste = await fetchProjects({ ...ctx, page: 1, limit: 1 })
      const total = erste.meta.total
      if (!total) return
      if (richtung === 'first' || total === 1) return oeffne(erste.data[0].id)
      const letzte = await fetchProjects({ ...ctx, page: total, limit: 1 })
      if (letzte.data[0]) oeffne(letzte.data[0].id)
    },
    [oeffne, user?.id],
  )

  const zurueck = useCallback(() => {
    if (!projektId) return void randProjekt('last').catch(() => undefined)
    if (nachbarn?.previous) oeffne(nachbarn.previous.id)
  }, [projektId, nachbarn, oeffne, randProjekt])
  const weiter = useCallback(() => {
    if (!projektId) return void randProjekt('first').catch(() => undefined)
    if (nachbarn?.next) oeffne(nachbarn.next.id)
  }, [projektId, nachbarn, oeffne, randProjekt])

  // ── Suche ───────────────────────────────────────────────────────────────
  const eingabe = useRef<HTMLInputElement>(null)
  const rahmen = useRef<HTMLDivElement>(null)
  const [suche, setSuche] = useState('')
  const [offen, setOffen] = useState(false)
  const [treffer, setTreffer] = useState<(RecentProject & { kunde?: string | null })[] | null>(null)
  const [markiert, setMarkiert] = useState(0)

  useEffect(() => {
    const q = suche.trim()
    if (!q) {
      setTreffer(null)
      return
    }
    let aktiv = true
    const t = setTimeout(() => {
      fetchProjects({ search: q, limit: 8, sortBy: 'projectNumber', sortDir: 'desc' })
        .then((r) => aktiv && setTreffer(r.data.map((p) => ({ id: p.id, projectNumber: p.projectNumber, name: p.name, kunde: p.client?.name }))))
        .catch(() => aktiv && setTreffer([]))
    }, 200)
    return () => {
      aktiv = false
      clearTimeout(t)
    }
  }, [suche])

  const eintraege: (RecentProject & { kunde?: string | null })[] = useMemo(
    () => (suche.trim() ? (treffer ?? []) : recent.filter((p) => p.id !== projektId).slice(0, 8)),
    [suche, treffer, recent, projektId],
  )
  useEffect(() => setMarkiert(0), [eintraege])

  const schliessen = () => {
    setOffen(false)
    setSuche('')
    eingabe.current?.blur()
  }
  const waehle = (id: string) => {
    schliessen()
    oeffne(id)
  }

  useEffect(() => {
    if (!offen) return
    const weg = (e: MouseEvent) => {
      if (rahmen.current && !rahmen.current.contains(e.target as Node)) {
        setOffen(false)
        setSuche('')
      }
    }
    document.addEventListener('mousedown', weg)
    return () => document.removeEventListener('mousedown', weg)
  }, [offen])

  // ── Tasten ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const ziel = e.target as HTMLElement | null
      if (ziel && (ziel.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(ziel.tagName))) return
      if (e.key === '/') {
        e.preventDefault()
        eingabe.current?.focus()
      } else if (e.key === '[') {
        zurueck()
      } else if (e.key === ']') {
        weiter()
      }
    }
    document.addEventListener('keydown', taste)
    return () => document.removeEventListener('keydown', taste)
  }, [zurueck, weiter])

  const tastenImFeld = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOffen(true)
      setMarkiert((m) => Math.min(m + 1, eintraege.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setMarkiert((m) => Math.max(m - 1, 0))
    } else if (e.key === 'Enter') {
      const p = eintraege[markiert]
      if (p) waehle(p.id)
    } else if (e.key === 'Escape') {
      schliessen()
    }
  }

  const titel = (p: { projectNumber: string; name: string } | null | undefined) =>
    p ? `${p.projectNumber} ${p.name}` : undefined
  const knopfKlein =
    'inline-flex h-[26px] w-[26px] items-center justify-center border border-border bg-background text-foreground hover:bg-muted disabled:cursor-default disabled:opacity-40 disabled:hover:bg-background'
  const leer = !projekt

  return (
    <div ref={rahmen} className="relative w-full space-y-1 rounded-md border border-border bg-background p-1 sm:w-[360px]">
      {/* Zeile 1: Suchfeld */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={eingabe}
          type="text"
          value={suche}
          onChange={(e) => {
            setSuche(e.target.value)
            setOffen(true)
          }}
          onFocus={() => setOffen(true)}
          onKeyDown={tastenImFeld}
          placeholder={`${terms.project} suchen …`}
          aria-label={`${terms.project} suchen`}
          className="h-[26px] w-full rounded-md border border-input bg-background pl-8 pr-8 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border px-1 text-[10px] text-muted-foreground">/</kbd>
      </div>

      {/* Zeile 2: Blättern, zuletzt, Neu — und die Aktionen des Projekts */}
      <div className="flex items-center gap-1.5">
        <div className="inline-flex">
          <button
            type="button"
            className={`${knopfKlein} rounded-l-md`}
            onClick={zurueck}
            disabled={!leer && !nachbarn?.previous}
            title={leer ? `Zum Ende der ${terms.list}  [` : nachbarn?.previous ? `← ${titel(nachbarn.previous)}  [` : 'Sonst nichts in der Liste'}
            aria-label="Zurück blättern"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={`${knopfKlein} -ml-px rounded-r-md`}
            onClick={weiter}
            disabled={!leer && !nachbarn?.next}
            title={leer ? `Zum Anfang der ${terms.list}  ]` : nachbarn?.next ? `→ ${titel(nachbarn.next)}  ]` : 'Sonst nichts in der Liste'}
            aria-label="Weiter blättern"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          className={`${knopfKlein} rounded-md`}
          onClick={() => zuletzt && oeffne(zuletzt.id)}
          disabled={!zuletzt}
          title={zuletzt ? `Zurück zu ${titel(zuletzt)}` : 'Noch nichts anderes geöffnet'}
          aria-label="Zuletzt geöffnet"
        >
          <Undo2 className="h-4 w-4" />
        </button>
        {!leer && nachbarn?.position && nachbarn.total > 1 && (
          <span className="text-xs text-muted-foreground" title="Stelle in der gefilterten Liste">
            {nachbarn.position}/{nachbarn.total}
          </span>
        )}

        <span className="flex-1" />

        {darfAnlegen && (
          <button
            type="button"
            className={
              leer
                ? 'inline-flex h-[26px] items-center gap-1 rounded-md bg-primary px-2.5 text-sm text-primary-foreground'
                : 'inline-flex h-[26px] items-center gap-1 rounded-md border border-border px-2.5 text-sm'
            }
            onClick={() => setAnlegen(true)}
          >
            <Plus className="h-4 w-4" /> Neu
          </button>
        )}
      </div>

      {/* Aufklappliste der Suche */}
      {offen && (
        <div
          className="absolute left-0 right-0 top-[calc(26px+0.5rem)] z-50 rounded-md border border-border p-1 shadow-lg"
          style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        >
          <p className="px-2 pb-1 pt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            {suche.trim() ? (treffer === null ? 'Sucht …' : `${eintraege.length} Treffer`) : 'Zuletzt geöffnet'}
          </p>
          {eintraege.length === 0 && (treffer !== null || !suche.trim()) && (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              {suche.trim() ? `${terms.none} gefunden` : `Noch keine ${terms.projectPlural} geöffnet`}
            </p>
          )}
          {eintraege.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setMarkiert(i)}
              onClick={() => waehle(p.id)}
              className={`flex w-full items-baseline justify-between gap-3 rounded px-2 py-1.5 text-left text-sm ${
                i === markiert ? 'bg-accent text-accent-foreground' : ''
              }`}
            >
              <span className="min-w-0 truncate">
                <span className="text-muted-foreground">{p.projectNumber}</span> {p.name}
              </span>
              {p.kunde && <span className="shrink-0 truncate text-xs text-muted-foreground">{p.kunde}</span>}
            </button>
          ))}
          <div className="mt-1 flex items-center justify-between border-t border-border px-2 pt-1.5 text-xs text-muted-foreground">
            <span>↑↓ wählen · Enter öffnen · Esc schließen</span>
            <Link to="/projects/list" className="text-primary hover:underline" onClick={() => setOffen(false)}>
              {terms.list} →
            </Link>
          </div>
        </div>
      )}

      {anlegen && (
        <ProjectFormDialog
          onClose={() => setAnlegen(false)}
          onSaved={(p) => {
            setAnlegen(false)
            // Wie in der Liste: weiter in den Projektdaten, Fokus im ersten
            // leeren Feld (AP-36 4.2).
            navigate(`/projects/${p.id}?tab=data&neu=1`)
          }}
        />
      )}
    </div>
  )
}
